<div class="space-y-8">
  <!-- What the plugin is: the two ways it shows on the merchant's own website,
       sketched with the live title and the brand colours. -->
  <section id="section-plugin-intro" hlmCard>
    <div hlmCardHeader>
      <h2 hlmCardTitle>Deine Gutschein-App</h2>
      <p hlmCardDescription>
        Mit dem Plugin verkaufst du Gutscheine direkt auf deiner eigenen Website: als schwebender
        Button oder mitten im Seiteninhalt. Hier legst du fest, was es anzeigt.
      </p>
      <div hlmCardAction>
        <a
          hlmBtn
          variant="outline"
          size="sm"
          [routerLink]="[]"
          [queryParams]="{ tab: 'integration' }">
          <ng-icon name="lucideCode" />
          Code zum Einbinden
        </a>
      </div>
    </div>

    <!-- Kept small: they explain the two placements, not the details. -->
    <div hlmCardContent class="grid max-w-md grid-cols-2 gap-3">
      <app-embed-preview
        variant="widget"
        compact
        caption="Schwebender Button"
        [primaryColor]="company?.voucher_app_primary_color"
        [textColor]="company?.voucher_app_text_color"
        [freeAmount]="!!sellForm.get('money_voucher_enabled')?.value"
        [fixedAmounts]="!!sellForm.get('fixed_voucher_amounts_enabled')?.value"
        [products]="!!sellForm.get('voucher_products_enabled')?.value"
        [title]="sellForm.get('voucher_app_title')?.value" />
      <app-embed-preview
        variant="inline"
        compact
        caption="Im Seiteninhalt"
        [primaryColor]="company?.voucher_app_primary_color"
        [textColor]="company?.voucher_app_text_color"
        [freeAmount]="!!sellForm.get('money_voucher_enabled')?.value"
        [fixedAmounts]="!!sellForm.get('fixed_voucher_amounts_enabled')?.value"
        [products]="!!sellForm.get('voucher_products_enabled')?.value"
        [title]="sellForm.get('voucher_app_title')?.value" />
    </div>
  </section>

  <form [formGroup]="sellForm">
    <section id="section-app-config" hlmCard>
      <div hlmCardHeader>
        <h2 hlmCardTitle>Inhalt des Plugins</h2>
        <p hlmCardDescription>
          Titel, Beschreibung und Gutschein-Arten, die deine Kunden im Plugin sehen.
        </p>
      </div>

      <div hlmCardContent class="space-y-6">
        <div class="space-y-4">
          <div hlmField>
            <label hlmFieldLabel for="voucher_app_title">Titel *</label>
            <input
              hlmInput
              id="voucher_app_title"
              type="text"
              formControlName="voucher_app_title"
              placeholder="z. B. Gutschein kaufen"
              class="w-full"
              [attr.aria-invalid]="isInvalid('voucher_app_title') ? 'true' : null" />
            @if (isInvalid('voucher_app_title')) {
              <p hlmFieldDescription class="text-destructive">Titel ist erforderlich</p>
            }
          </div>

          <div hlmField>
            <label hlmFieldLabel for="voucher_app_description">Beschreibung *</label>
            <textarea
              hlmTextarea
              id="voucher_app_description"
              rows="4"
              formControlName="voucher_app_description"
              placeholder="Beschreibe dein Gutschein-Angebot…"
              class="w-full"
              [attr.aria-invalid]="isInvalid('voucher_app_description') ? 'true' : null"></textarea>
            @if (isInvalid('voucher_app_description')) {
              <p hlmFieldDescription class="text-destructive">Beschreibung ist erforderlich</p>
            }
          </div>

          <div hlmField>
            <label hlmFieldLabel for="minimum_voucher_amount">Mindest-Gutscheinbetrag (€) *</label>
            <input
              hlmInput
              id="minimum_voucher_amount"
              type="number"
              min="2"
              step="1"
              inputmode="numeric"
              formControlName="minimum_voucher_amount"
              placeholder="10"
              class="w-full"
              [attr.aria-invalid]="isInvalid('minimum_voucher_amount') ? 'true' : null" />
            @if (isInvalid('minimum_voucher_amount')) {
              <p hlmFieldDescription class="text-destructive">
                {{ minimumAmountError }}
              </p>
            } @else {
              <p hlmFieldDescription>
                Der Mindestbetrag, den Kunden bei einem frei gewählten Betrag ausgeben müssen.
              </p>
            }
          </div>
        </div>

        <div hlmSeparator></div>

        <div class="space-y-3">
          <h3 hlmSmall>Gutschein-Arten</h3>

          <div hlmItem variant="outline" class="items-start">
            <div hlmItemContent>
              <span hlmItemTitle>Wert-Gutscheine mit freiem Betrag</span>
              <span hlmItemDescription>
                Kunden geben den Betrag selbst ein, ab dem Mindest-Gutscheinbetrag.
              </span>
            </div>
            <div hlmItemActions>
              <hlm-switch
                inputId="money_voucher_enabled"
                aria-label="Wert-Gutscheine mit freiem Betrag"
                formControlName="money_voucher_enabled" />
            </div>
          </div>

          <div hlmItem variant="outline" class="flex-wrap items-start">
            <div hlmItemContent>
              <span hlmItemTitle>Wert-Gutscheine mit festen Beträgen</span>
              <span hlmItemDescription>
                Kunden wählen einen der Beträge, die du vorgibst. Mit beiden Wert-Gutschein-Arten
                zeigt deine Gutschein-App zwei Karten.
              </span>
            </div>
            <div hlmItemActions>
              <hlm-switch
                inputId="fixed_voucher_amounts_enabled"
                aria-label="Wert-Gutscheine mit festen Beträgen"
                formControlName="fixed_voucher_amounts_enabled" />
            </div>

            @if (sellForm.get('fixed_voucher_amounts_enabled')?.value) {
              <div class="w-full space-y-3">
                @if (fixedAmounts.length) {
                  <div class="flex flex-wrap gap-2">
                    @for (amount of fixedAmounts; track amount) {
                      <span hlmBadge variant="secondary" class="gap-1 pe-1 text-sm">
                        {{ formatEuros(amount) }}
                        <button
                          type="button"
                          class="hover:bg-muted-foreground/20 rounded-sm p-0.5"
                          [attr.aria-label]="formatEuros(amount) + ' entfernen'"
                          (click)="removeFixedAmount(amount)">
                          <ng-icon name="lucideX" size="14" />
                        </button>
                      </span>
                    }
                  </div>
                }

                @if (fixedAmounts.length < maxFixedAmounts) {
                  <div class="flex gap-2">
                    <input
                      hlmInput
                      type="text"
                      inputmode="decimal"
                      placeholder="Betrag in €, z. B. 25"
                      aria-label="Fester Betrag in Euro"
                      class="w-48"
                      [value]="newFixedAmount"
                      (input)="newFixedAmount = $any($event.target).value; fixedAmountError = ''"
                      (keydown.enter)="$event.preventDefault(); addFixedAmount()" />
                    <button hlmBtn type="button" variant="outline" (click)="addFixedAmount()">
                      <ng-icon name="lucidePlus" />
                      Hinzufügen
                    </button>
                  </div>
                }

                @if (fixedAmountError) {
                  <p hlmFieldDescription class="text-destructive">{{ fixedAmountError }}</p>
                } @else if (fixedAmountsMissing) {
                  <p hlmFieldDescription class="text-destructive">
                    Bitte gib mindestens einen Betrag an.
                  </p>
                } @else {
                  <p hlmFieldDescription>
                    Bis zu {{ maxFixedAmounts }} Beträge, je mindestens 2 €. Sie gelten auch unter
                    dem Mindest-Gutscheinbetrag.
                  </p>
                }
              </div>
            }
          </div>

          <div hlmItem variant="outline" class="items-start">
            <div hlmItemContent>
              <span hlmItemTitle>Produkt-Gutscheine</span>
              <span hlmItemDescription>
                Kunden können Gutscheine für spezifische Produkte oder Dienstleistungen kaufen.
              </span>
            </div>
            <div hlmItemActions>
              <hlm-switch
                inputId="voucher_products_enabled"
                aria-label="Produkt-Gutscheine"
                formControlName="voucher_products_enabled" />
            </div>
          </div>

          <p hlmMuted class="flex items-start gap-1.5 text-xs">
            <ng-icon name="lucideInfo" class="mt-0.5 shrink-0" />
            Mindestens eine Gutschein-Art muss aktiviert sein.
          </p>

          <a hlmItem variant="outline" [routerLink]="['/products']">
            <div hlmItemMedia variant="icon"><ng-icon name="lucidePackage" /></div>
            <div hlmItemContent>
              <span hlmItemTitle>Gutschein-Produkte verwalten</span>
              <span hlmItemDescription>Produkte anlegen, bearbeiten und aktivieren</span>
            </div>
            <div hlmItemActions>
              <ng-icon name="lucideChevronRight" class="text-muted-foreground" />
            </div>
          </a>
        </div>
      </div>

      <div hlmCardFooter class="justify-end border-t pt-4">
        <app-save-button
          #saveButton
          size="sm"
          [disabled]="!sellForm.valid || !formChanged || fixedAmountsMissing"
          [loading]="loading"
          (clickEvent)="onSubmit()" />
      </div>
    </section>
  </form>

  <!-- Colours (and name, logo, banner) live on "Marke & Branding": the plugin only uses them. -->
  <a
    hlmItem
    variant="outline"
    [routerLink]="[]"
    [queryParams]="{ tab: 'brand' }"
    fragment="section-colors">
    <div hlmItemMedia aria-hidden="true">
      <span class="flex -space-x-1.5">
        <span
          class="border-background size-6 rounded-full border-2"
          [style.background-color]="company?.voucher_app_primary_color || '#4b5ae4'"></span>
        <span
          class="border-background size-6 rounded-full border-2 shadow-xs"
          [style.background-color]="company?.voucher_app_text_color || '#FFFFFF'"></span>
      </span>
    </div>
    <div hlmItemContent>
      <span hlmItemTitle>Farben des Plugins</span>
      <span hlmItemDescription>
        Kommen aus deiner Marke, wie auf Webseite und Gutschein-PDF. Unter „Marke & Branding“
        ändern.
      </span>
    </div>
    <div hlmItemActions>
      <ng-icon name="lucideChevronRight" class="text-muted-foreground" />
    </div>
  </a>
</div>
